import { z } from 'zod';
import { colorCodeSchema } from './catalog';
import { normalizedUuidSchema, UUID_PATTERN, uuidSchema } from './common';
import { MAX_IMAGE_UPLOAD_BYTES } from './image-asset';

// ---------------------------------------------------------------------------
// 폰트·잉크 색상 계약(결정 D1·D9) — apps/api/src/modules/editor/text-options 가 쓴다.
// 공개 editor.textOptions.listForProduct 는 PUBLISHED 상품의 허용 목록(삭제 제외)만 돌려주고,
// 어드민 productManagement.fonts.*·productManagement.inkColors.* 가 마스터와 상품별 허용 목록을 관리한다.
// 폰트 파일 업로드는 무상태다 — presignUpload 가 fontId 를 발급해 임시 키에 올리게 하고, create 가 검증·확정한다.
// 디자인 문서의 fontId·inkColorId 참조 검증(400 문구)은 design.ts.
// ---------------------------------------------------------------------------

/**
 * fonts.format 허용값 — fonts 의 chk_fonts_format 과 같다.
 * @repo/database 의 FontFormat 과 동치여야 한다 — 두 패키지는 서로 의존하지 않으므로 apps/api 스펙이 동치를 고정한다.
 */
export const FONT_FORMATS = ['woff2', 'ttf', 'otf'] as const;
export type FontFormat = (typeof FONT_FORMATS)[number];
/** presigned PUT·finalizeObject 에 쓸 콘텐츠 타입(RFC 8081). 형식과 1:1 이라 확장자가 곧 키다. */
export const FONT_CONTENT_TYPES = {
  woff2: 'font/woff2',
  ttf: 'font/ttf',
  otf: 'font/otf',
} as const satisfies Record<FontFormat, string>;
export type FontContentType = (typeof FONT_CONTENT_TYPES)[FontFormat];

/** 폰트 파일 상한. S3StorageAdapter.finalizeObject 의 MinIO 경로가 10MiB 를 하드코딩하므로 이미지 상한과 같이 둔다. */
export const MAX_FONT_UPLOAD_BYTES = MAX_IMAGE_UPLOAD_BYTES;

/** 최종 키 접두사 — fonts 의 chk_fonts_file_key_prefix(`LIKE 'fonts/%'`)와 같다. IAM FontObjects(`fonts/*`). */
export const FONT_KEY_PREFIX = 'fonts/';
/** 임시 키 접두사. IAM TemporaryUploads(`tmp/*`)·수명주기 `tmp/`(1일) 아래에 든다(ADR-0008). */
export const FONT_TEMP_PREFIX = 'tmp/fonts/';

const FONT_FORMAT_ALTERNATION = FONT_FORMATS.join('|');

/** 최종 키 형식 전체 — `fonts/{uuid}.{woff2|ttf|otf}`. 그룹 1 = fonts.id, 그룹 2 = format. 세그먼트를 고정해 `..` 가 낄 자리를 없앤다. */
export const FONT_KEY_PATTERN = new RegExp(
  `^${FONT_KEY_PREFIX}(${UUID_PATTERN})\\.(${FONT_FORMAT_ALTERNATION})$`,
);
/** 임시 키 형식 — `tmp/fonts/{uuid}.{woff2|ttf|otf}`. 그룹 1 = fonts.id. */
export const FONT_TEMPORARY_KEY_PATTERN = new RegExp(
  `^${FONT_TEMP_PREFIX}(${UUID_PATTERN})\\.(${FONT_FORMAT_ALTERNATION})$`,
);

/** 키 생성(api text-options)과 키 형식(위 패턴)이 같은 표를 보도록 조립도 여기서 한다. */
export const fontFileKeyFor = (input: {
  readonly fontId: string;
  readonly format: FontFormat;
}): string => `${FONT_KEY_PREFIX}${input.fontId}.${input.format}`;
export const fontTemporaryKeyFor = (input: {
  readonly fontId: string;
  readonly format: FontFormat;
}): string => `${FONT_TEMP_PREFIX}${input.fontId}.${input.format}`;

export const fontKeySchema = z.string().regex(FONT_KEY_PATTERN);
const fontFormatSchema = z.enum(FONT_FORMATS);

// ---------------------------------------------------------------------------
// 이름·상한
// ---------------------------------------------------------------------------

/** fonts.family_name — chk_fonts_family_name_length(1~100) 와 같다. */
export const MAX_FONT_FAMILY_NAME_LENGTH = 100;
export const fontFamilyNameSchema = z.string().trim().min(1).max(MAX_FONT_FAMILY_NAME_LENGTH);
/** ink_colors.name — chk_ink_colors_name_length(1~50) 와 같다. */
export const MAX_INK_COLOR_NAME_LENGTH = 50;
export const inkColorNameSchema = z.string().trim().min(1).max(MAX_INK_COLOR_NAME_LENGTH);
/** 상품 하나에 허용하는 폰트·잉크 각각의 상한(setForProduct 입력). */
export const MAX_PRODUCT_TEXT_OPTIONS = 50;

const productInputSchema = z.strictObject({ productId: normalizedUuidSchema });
const idInputSchema = z.strictObject({ id: normalizedUuidSchema });

// ---------------------------------------------------------------------------
// 어드민 — 폰트 (productManagement.fonts.*)
// ---------------------------------------------------------------------------

export const presignFontUploadInputSchema = z.strictObject({ format: fontFormatSchema });
export type PresignFontUploadInput = z.infer<typeof presignFontUploadInputSchema>;

/** 응답은 non-strict — 라우터 .output() 이 StoragePort.presignPut 의 여분 key 를 떨어뜨린다. 확정 창은 없다(tmp 가 사라지면 410). */
export const presignFontUploadResultSchema = z.object({
  /** 서버가 발급한 fonts.id — create 가 이 값으로 임시 키와 최종 키를 만든다. */
  fontId: uuidSchema,
  uploadUrl: z.url(),
  temporaryKey: z.string().regex(FONT_TEMPORARY_KEY_PATTERN),
  expiresInSeconds: z.number().int().positive(),
});
export type PresignFontUploadResult = z.infer<typeof presignFontUploadResultSchema>;

/** fileKey·sha256 은 서버가 정한다 — 입력으로 받지 않는다. */
export const createFontInputSchema = z.strictObject({
  fontId: normalizedUuidSchema,
  format: fontFormatSchema,
  familyName: fontFamilyNameSchema,
});
export type CreateFontInput = z.infer<typeof createFontInputSchema>;

/** format·fileKey·sha256 은 불변(트리거) — familyName 만 바꾼다. */
export const renameFontInputSchema = z.strictObject({
  id: normalizedUuidSchema,
  familyName: fontFamilyNameSchema,
});
export type RenameFontInput = z.infer<typeof renameFontInputSchema>;
export const deleteFontInputSchema = idInputSchema;
export type DeleteFontInput = z.infer<typeof deleteFontInputSchema>;

/** 어드민 목록 항목 — 살아 있는 행만(deleted_at IS NULL). sha256 은 싣지 않는다. */
export const fontSchema = z.object({
  id: uuidSchema,
  familyName: fontFamilyNameSchema,
  format: fontFormatSchema,
  fileKey: fontKeySchema,
});
export type Font = z.infer<typeof fontSchema>;
export const fontListSchema = z.array(fontSchema);

export const listProductFontsInputSchema = productInputSchema;
export type ListProductFontsInput = z.infer<typeof listProductFontsInputSchema>;
/** 전체 교체 — 빈 배열은 전부 해제. 삭제된 마스터 id 가 섞이면 서비스가 400. */
export const setProductFontsInputSchema = z.strictObject({
  productId: normalizedUuidSchema,
  fontIds: z.array(normalizedUuidSchema).max(MAX_PRODUCT_TEXT_OPTIONS),
});
export type SetProductFontsInput = z.infer<typeof setProductFontsInputSchema>;

// ---------------------------------------------------------------------------
// 폰트 업로드·등록 거부 문구. 만료(410)는 tRPC 에 GONE 이 없어 BAD_REQUEST 로, 409 두 종은 같은 CONFLICT 로
// 나가므로 코드로는 갈리지 않는다 — 어드민 화면은 message 를 이 상수와 대조해 안내를 가른다(고객 이미지의 결정 E1 과 같은 이유).
// ---------------------------------------------------------------------------

/** 410 — 임시 객체(tmp/fonts/{fontId}.{format})가 없다(수명주기 1일). presignUpload 부터 다시 한다. */
export const FONT_UPLOAD_EXPIRED_MESSAGE = '임시 업로드가 만료되었습니다. 다시 업로드해 주세요.';
/** 409 — 살아 있는 폰트와 패밀리명이 겹친다(부분 유니크). 같은 fontId 로 이름만 바꿔 create 를 다시 부를 수 있다. */
export const FONT_NAME_CONFLICT_MESSAGE = '이미 존재하는 폰트 이름입니다.';
/** 409 — 같은 fontId(id·file_key)가 이미 등록됐다. 새로 업로드해야 한다. */
export const FONT_FILE_CONFLICT_MESSAGE = '이미 등록된 폰트 파일입니다.';

// ---------------------------------------------------------------------------
// 어드민 — 잉크 색상 (productManagement.inkColors.*)
// ---------------------------------------------------------------------------

/** colorCode 는 상품 색상 옵션과 같은 `#RRGGBB`(catalog colorCodeSchema) — ink_colors 의 chk_ink_colors_color_code_format 과 같다. */
export const createInkColorInputSchema = z.strictObject({
  name: inkColorNameSchema,
  colorCode: colorCodeSchema,
});
export type CreateInkColorInput = z.infer<typeof createInkColorInputSchema>;

/** 409 — 살아 있는 잉크와 이름이 겹친다(부분 유니크). 등록·이름 변경이 같은 문구로 거부한다(폰트 409 와 같은 이유로 상수). */
export const INK_COLOR_NAME_CONFLICT_MESSAGE = '이미 존재하는 잉크 색상 이름입니다.';

/** colorCode 는 불변 — name 만 바꾼다. */
export const renameInkColorInputSchema = z.strictObject({
  id: normalizedUuidSchema,
  name: inkColorNameSchema,
});
export type RenameInkColorInput = z.infer<typeof renameInkColorInputSchema>;
export const deleteInkColorInputSchema = idInputSchema;
export type DeleteInkColorInput = z.infer<typeof deleteInkColorInputSchema>;

/** 어드민 목록 항목 — 살아 있는 행만. 공개 응답과 모양이 같다. */
export const inkColorSchema = z.object({
  id: uuidSchema,
  name: inkColorNameSchema,
  colorCode: colorCodeSchema,
});
export type InkColor = z.infer<typeof inkColorSchema>;
export const inkColorListSchema = z.array(inkColorSchema);

export const listProductInkColorsInputSchema = productInputSchema;
export type ListProductInkColorsInput = z.infer<typeof listProductInkColorsInputSchema>;
export const setProductInkColorsInputSchema = z.strictObject({
  productId: normalizedUuidSchema,
  inkColorIds: z.array(normalizedUuidSchema).max(MAX_PRODUCT_TEXT_OPTIONS),
});
export type SetProductInkColorsInput = z.infer<typeof setProductInkColorsInputSchema>;

// ---------------------------------------------------------------------------
// 어드민 — id 로 이름 찾기 (productManagement.fonts.listByIds·productManagement.inkColors.listByIds, 결정 D12)
// 견적 사본(design_body)에는 fontId·inkColorId 만 있다. 견적 뒤 지운 폰트·잉크도 제작에 이름이 필요하므로
// 삭제된 행도 돌려주고 deleted 로 표시한다. 없는 id 는 결과에서 빠진다 — 화면은 그 id 를 그대로 보여 준다.
// 이름은 지금 이름이다(결정 D13).
// ---------------------------------------------------------------------------

/** 한 번에 찾는 id 상한. 디자인 하나가 쓰는 폰트·잉크는 보통 상품 허용 목록 상한(50) 안이다. */
export const MAX_TEXT_OPTION_LOOKUP_IDS = 100;

const lookupByIdsInputSchema = z.strictObject({
  ids: z.array(normalizedUuidSchema).min(1).max(MAX_TEXT_OPTION_LOOKUP_IDS),
});

export const listFontsByIdsInputSchema = lookupByIdsInputSchema;
export type ListFontsByIdsInput = z.infer<typeof listFontsByIdsInputSchema>;
export const listInkColorsByIdsInputSchema = lookupByIdsInputSchema;
export type ListInkColorsByIdsInput = z.infer<typeof listInkColorsByIdsInputSchema>;

/** 폰트 이름 찾기 항목. 응답은 non-strict — .output() 이 행의 여분 키를 떨어뜨린다. */
export const fontLookupSchema = z.object({
  id: uuidSchema,
  familyName: fontFamilyNameSchema,
  deleted: z.boolean(),
});
export type FontLookup = z.infer<typeof fontLookupSchema>;
export const fontLookupListSchema = z.array(fontLookupSchema);

/** 잉크 이름 찾기 항목 — 견본을 칠할 colorCode 를 함께 싣는다. */
export const inkColorLookupSchema = z.object({
  id: uuidSchema,
  name: inkColorNameSchema,
  colorCode: colorCodeSchema,
  deleted: z.boolean(),
});
export type InkColorLookup = z.infer<typeof inkColorLookupSchema>;
export const inkColorLookupListSchema = z.array(inkColorLookupSchema);

// ---------------------------------------------------------------------------
// 공개 — editor.textOptions.listForProduct
// ---------------------------------------------------------------------------

export const listProductTextOptionsInputSchema = productInputSchema;
export type ListProductTextOptionsInput = z.infer<typeof listProductTextOptionsInputSchema>;

/** 브라우저가 @font-face 로 읽을 서명 URL(VIEW_URL_EXPIRES_IN_SECONDS 동안 유효). 저장 키는 싣지 않는다. */
export const publicFontSchema = z.object({
  id: uuidSchema,
  familyName: fontFamilyNameSchema,
  format: fontFormatSchema,
  fileUrl: z.url(),
});
export type PublicFont = z.infer<typeof publicFontSchema>;
export const publicInkColorSchema = inkColorSchema;
export type PublicInkColor = z.infer<typeof publicInkColorSchema>;

/**
 * PUBLISHED 상품의 허용 폰트·잉크(삭제 제외). 응답은 non-strict — .output() 이 행의 여분 키를 떨어뜨린다.
 * 배열에 상한을 두지 않는다(결정 E8) — DB 에는 행 수 제약이 없어 시드·직접 INSERT 로 입력 상한(MAX_PRODUCT_TEXT_OPTIONS)을
 * 넘긴 연결이 있어도 공개 조회가 500 으로 깨지지 않게 한다.
 */
export const publicTextOptionsSchema = z.object({
  fonts: z.array(publicFontSchema),
  inkColors: z.array(publicInkColorSchema),
});
export type PublicTextOptions = z.infer<typeof publicTextOptionsSchema>;
